import { z } from "zod";
import { REGISTRY_REBUS_IDS } from "../registryRebusy";
import { CurrencySchema, LocaleSchema } from "./user";
import { GuestGroupSchema } from "./guest";
import { GuestAlbumConfigSchema, GuestGalleryStatsSchema } from "./guest-photo";
import { SeatingCanvasInsetSchema, DEFAULT_SEATING_CANVAS_INSET, SEATING_CANVAS_INSET, clampSeatingCanvasInset, } from "./seating-canvas";
export { SeatingCanvasInsetSchema, DEFAULT_SEATING_CANVAS_INSET, SEATING_CANVAS_INSET, clampSeatingCanvasInset, };
// ─── Seating canvas (re-exported from seating-canvas.ts) ─────────────────────
// ─── Website configuration ─────────────────────────────────────────────────
/** Five-colour palette shared by preset and custom themes. */
export const WebsitePaletteSchema = z.object({
    bg: z.string(),
    ink: z.string(),
    ac: z.string(),
    muted: z.string(),
    alt: z.string(),
});
export const PRESET_WEBSITE_THEMES = ["classic", "rustic", "garden", "blush"];
export const WebsiteThemeSchema = z.enum([...PRESET_WEBSITE_THEMES, "custom"]);
/** Maps retired theme ids to their replacements (e.g. after preset renames). */
export const LEGACY_WEBSITE_THEME_MAP = {
    modern: "garden",
    minimal: "blush",
};
export function normalizeWebsiteTheme(raw) {
    if (!raw)
        return "classic";
    if (raw === "custom")
        return "custom";
    if (PRESET_WEBSITE_THEMES.includes(raw))
        return raw;
    const legacy = LEGACY_WEBSITE_THEME_MAP[raw];
    if (legacy)
        return legacy;
    return "classic";
}
export const WebsiteSectionIdSchema = z.enum([
    "hero",
    "countdown",
    "story",
    "schedule",
    "venue",
    "transport",
    "hotels",
    "dresscode",
    "registry",
    "gallery",
    "guestGallery",
    "faq",
    "rsvp",
]);
// Structured section content. All optional — a section falls back to the
// free-text `content` map (and then a built-in default) when its
// structured field is empty.
export const ScheduleItemSchema = z.object({
    time: z.string(),
    label: z.string(),
    /** Optional day/date label, e.g. "Sobota, 14 czerwca 2025". Groups items under a header. */
    date: z.string().optional(),
});
export const WebsiteVenueSchema = z.object({
    address: z.string(),
    mapUrl: z.string().nullable().optional(),
});
export const GalleryPhotoSchema = z.object({
    storagePath: z.string(),
    caption: z.string().nullable().optional(),
    order: z.number().int().nonnegative().default(0),
});
/** Built-in pictogram id or uploaded Storage path. Max 5 items total. */
export const RegistryRebusIdSchema = z.enum(REGISTRY_REBUS_IDS);
export const RegistryPhotoSchema = z
    .object({
    storagePath: z.string().optional(),
    assetId: RegistryRebusIdSchema.optional(),
    caption: z.string().nullable().optional(),
    order: z.number().int().nonnegative().default(0),
})
    .refine((p) => Boolean(p.storagePath || p.assetId), {
    message: "Registry item requires storagePath or assetId",
});
export const GalleryDisplayModeSchema = z.enum(["carousel", "grid"]);
/**
 * Photo in the dedicated full-page gallery album (`/w/:handle/galeria`).
 * Separate from the inline `gallery` strip so the couple can curate a tight
 * teaser on the site while loading the album with hundreds of photos.
 *
 * `width`/`height` are captured at upload time so the masonry layout can
 * reserve space and avoid layout shift before the image decodes.
 */
export const GalleryAlbumPhotoSchema = z.object({
    id: z.string().min(1),
    storagePath: z.string(),
    caption: z.string().nullable().optional(),
    order: z.number().int().nonnegative().default(0),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
/** Free-plan cap mirrors the inline gallery. Premium lifts to MAX_PREMIUM. */
export const GALLERY_ALBUM_MAX_FREE_PHOTOS = 20;
export const GALLERY_ALBUM_MAX_PREMIUM_PHOTOS = 200;
export const FaqItemSchema = z.object({
    question: z.string(),
    /** TipTap HTML */
    answer: z.string(),
});
export const FaqDisplayModeSchema = z.enum(["accordion", "list"]);
export const HeroImagePositionSchema = z.object({
    x: z.number().min(0).max(100).default(50),
    y: z.number().min(0).max(100).default(50),
});
export const DEFAULT_HERO_IMAGE_POSITION = { x: 50, y: 50 };
export const DEFAULT_STORY_TITLE = "Nasza historia";
/** Sections visible by default when no website config has been saved yet (all except dresscode, gallery, guestGallery, faq). */
export const DEFAULT_VISIBLE_SECTIONS = [
    "hero",
    "countdown",
    "story",
    "schedule",
    "venue",
    "transport",
    "hotels",
    "registry",
    "rsvp",
];
export const DEFAULT_HERO_EYEBROW = "Save the date";
/** Keys into the active theme palette (`bg`, `ink`, `ac`, `muted`, `alt`). */
export const WebsitePaletteColorSchema = z.enum(["bg", "ink", "ac", "muted", "alt"]);
/** Optional per-locale overrides for guest-facing website content. */
export const WebsiteLocaleBundleSchema = z.object({
    content: z.record(WebsiteSectionIdSchema, z.string().nullable()).optional(),
    storyTitle: z.string().optional(),
    heroEyebrow: z.string().optional(),
    schedule: z.array(ScheduleItemSchema).optional(),
    faq: z.array(FaqItemSchema).optional(),
});
export const WebsiteConfigSchema = z.object({
    theme: z
        .preprocess((v) => normalizeWebsiteTheme(typeof v === "string" ? v : undefined), WebsiteThemeSchema)
        .default("classic"),
    /** User-defined palette when `theme` is `"custom"`. */
    customPalette: WebsitePaletteSchema.optional(),
    /** Section IDs that are toggled ON. Default: all except dresscode and gallery. */
    visibleSections: z.array(WebsiteSectionIdSchema).default([...DEFAULT_VISIBLE_SECTIONS]),
    /** Display order of sections on the rendered site. Omitted = canonical order. */
    sectionOrder: z.array(WebsiteSectionIdSchema).optional(),
    /** Legacy plaintext access code. Superseded by passwordHash; migrated on next save. */
    password: z.string().nullable().optional(),
    /** Hashed guest-access code. Compared server-side; never sent to clients. */
    passwordHash: z.string().nullable().optional(),
    /** Free-text content keyed by section id. */
    content: z.record(WebsiteSectionIdSchema, z.string().nullable()).default({}),
    /** Display title for the story section. Falls back to DEFAULT_STORY_TITLE when empty. */
    storyTitle: z.string().default(DEFAULT_STORY_TITLE),
    /**
     * Hero eyebrow text above the couple names. Omitted = DEFAULT_HERO_EYEBROW.
     * Empty string = hidden.
     */
    heroEyebrow: z.string().optional(),
    /** When true, coupleName2 appears before coupleName1 in the hero title. */
    heroNamesSwapped: z.boolean().default(false),
    /** Structured day-of schedule rows. Falls back to content.schedule when empty. */
    schedule: z.array(ScheduleItemSchema).default([]),
    /** Structured venue details. Falls back to content.venue when null. */
    venue: WebsiteVenueSchema.nullable().optional(),
    /** Gift-registry pictograms and/or uploaded photos. Max 5. Text in content.registry. */
    registry: z
        .preprocess((val) => {
        if (!Array.isArray(val))
            return [];
        return val.filter((item) => RegistryPhotoSchema.safeParse(item).success);
    }, z.array(RegistryPhotoSchema))
        .default([]),
    /** Gallery photos (Storage paths). Max 20 on the free plan. */
    gallery: z.array(GalleryPhotoSchema).default([]),
    /** How the gallery section is rendered on the public site. */
    galleryDisplayMode: GalleryDisplayModeSchema.default("grid"),
    /**
     * Dedicated full-page album at `/w/:handle/galeria`. When `enabled` is
     * true the inline gallery section grows a "View full gallery" CTA and
     * the page is reachable. Independent of `visibleSections` — the album
     * is its own surface, not a section in the long-scroll site.
     */
    galleryAlbum: z
        .object({
        enabled: z.boolean().default(false),
        /** ID of the photo used as the parallax hero. Null = first photo. */
        heroPhotoId: z.string().nullable().default(null),
        /** Focal point for the hero image within the parallax frame (%). */
        heroPosition: HeroImagePositionSchema.optional(),
        photos: z.array(GalleryAlbumPhotoSchema).max(GALLERY_ALBUM_MAX_PREMIUM_PHOTOS).default([]),
        updatedAt: z.string().datetime().optional(),
    })
        .optional(),
    /**
     * Guest photo album — uploads happen via QR/`/upload/:token`, moderated
     * by the couple, optionally revealed on the public site. Three orthogonal
     * toggles in `GuestAlbumConfig`. Absent = disabled (default).
     */
    guestAlbum: GuestAlbumConfigSchema.optional(),
    /** FAQ question/answer pairs. Falls back to content.faq when empty. */
    faq: z.array(FaqItemSchema).default([]),
    /** How the FAQ section is rendered on the public site. */
    faqDisplayMode: FaqDisplayModeSchema.default("accordion"),
    /**
     * RSVP cut-off (ISO timestamp). After it, the public RSVP form shows a
     * "closed" message instead of accepting responses. Null = no deadline.
     */
    rsvpDeadline: z.string().datetime().nullable().optional(),
    published: z.boolean().default(false),
    publishedAt: z.string().datetime().nullable().optional(),
    /** When false, the hero image block is hidden on the public site. Default on. */
    heroImageEnabled: z.boolean().default(true),
    /** Hero background image Storage path. Null = use striped placeholder. */
    heroImagePath: z.string().nullable().optional(),
    /** Focal point for the hero image within its frame (%). Default center. */
    heroImagePosition: HeroImagePositionSchema.optional(),
    /** Font pair identifier. Controls display + body fonts on the rendered site. */
    fontPair: z.enum(["serif", "modern", "romantic"]).default("serif"),
    /** Per-section background color from the active theme palette. Omitted = built-in default. */
    sectionBackgrounds: z.record(WebsiteSectionIdSchema, WebsitePaletteColorSchema).default({}),
    /**
     * Locales guests may switch to on the public site. Empty → primary only
     * (`wedding.settings.language`). Primary is always available even if omitted.
     */
    enabledLocales: z.array(LocaleSchema).default([]),
    /** Optional per-locale content overrides. Omitted fields fall back to primary. */
    localized: z.record(LocaleSchema, WebsiteLocaleBundleSchema).default({}),
});
/** Canonical section order, used when `sectionOrder` is unset. */
export const ALL_WEBSITE_SECTIONS = [
    "hero",
    "countdown",
    "story",
    "schedule",
    "venue",
    "transport",
    "hotels",
    "dresscode",
    "registry",
    "gallery",
    "guestGallery",
    "faq",
    "rsvp",
];
/** Resolve the ordered list of sections to render (visible ∩ order). */
export function resolveSiteSections(config) {
    const visible = new Set(config.visibleSections);
    const order = config.sectionOrder?.length ? config.sectionOrder : ALL_WEBSITE_SECTIONS;
    const out = [];
    for (const s of order)
        if (visible.has(s) && !out.includes(s))
            out.push(s);
    for (const s of ALL_WEBSITE_SECTIONS)
        if (visible.has(s) && !out.includes(s))
            out.push(s);
    return out;
}
// ─── Public (guest-facing) site payload ────────────────────────────────────
// The sanitized shape the `api` Cloud Function returns for /w/{handle}.
// Strips ownerId / partnerId / passwordHash / guest data.
export const PublicGalleryPhotoSchema = z.object({
    url: z.string(),
    caption: z.string().nullable(),
});
export const PublicRegistryPhotoSchema = PublicGalleryPhotoSchema;
export const PublicGalleryAlbumPhotoSchema = z.object({
    id: z.string(),
    url: z.string(),
    caption: z.string().nullable(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
});
export const PublicGalleryAlbumSchema = z.object({
    enabled: z.boolean(),
    heroUrl: z.string().nullable(),
    heroPosition: HeroImagePositionSchema.optional(),
    photos: z.array(PublicGalleryAlbumPhotoSchema),
});
export const PublicSiteSchema = z.object({
    handle: z.string(),
    coupleName1: z.string(),
    coupleName2: z.string(),
    weddingDate: z.string(),
    venueName: z.string().nullable(),
    rsvpDeadline: z.string().nullable(),
    theme: z.preprocess((v) => normalizeWebsiteTheme(typeof v === "string" ? v : undefined), WebsiteThemeSchema),
    /** Resolved custom palette when `theme` is `"custom"`. */
    customPalette: WebsitePaletteSchema.optional(),
    /** True when a guest-access code is set; content fields come back empty. */
    locked: z.boolean(),
    /** Ordered, visible section ids. Empty when locked. */
    sections: z.array(WebsiteSectionIdSchema),
    content: z.record(WebsiteSectionIdSchema, z.string().nullable()),
    /** Display title for the story section. */
    storyTitle: z.string().default(DEFAULT_STORY_TITLE),
    /** Hero eyebrow text. Omitted = default; empty string = hidden. */
    heroEyebrow: z.string().optional(),
    /** When true, coupleName2 appears before coupleName1 in the hero title. */
    heroNamesSwapped: z.boolean().default(false),
    schedule: z.array(ScheduleItemSchema),
    venue: WebsiteVenueSchema.nullable(),
    registry: z.array(PublicRegistryPhotoSchema),
    gallery: z.array(PublicGalleryPhotoSchema),
    /** Gallery layout on the rendered site. */
    galleryDisplayMode: GalleryDisplayModeSchema.default("grid"),
    /**
     * Dedicated full-page album. When `enabled: true` the inline gallery
     * section renders a "Zobacz całą galerię" CTA pointing at the album page.
     * When disabled, `photos` is empty and the album route returns the
     * unpublished placeholder.
     */
    galleryAlbum: PublicGalleryAlbumSchema.default({
        enabled: false,
        heroUrl: null,
        photos: [],
    }),
    /** Approved guest-uploaded photos, capped at the latest 200. */
    guestGallery: z.array(PublicGalleryPhotoSchema).default([]),
    /** Guest gallery layout. Matches `wedding.website.guestAlbum.displayMode`. */
    guestGalleryDisplayMode: GalleryDisplayModeSchema.default("grid"),
    /**
     * True when all three toggles align (section visible, album enabled, guest
     * reveal on). The public API never returns the section in `sections[]`
     * when this is false; the builder preview uses it together with
     * `previewMode` to decide between the live section and the Reveal placeholder.
     */
    guestGalleryVisible: z.boolean().default(false),
    /** Activity totals for the builder preview's reveal-off placeholder. */
    guestGalleryStats: GuestGalleryStatsSchema.nullable().default(null),
    /** Public `/upload/{token}` URL surfaced to guests via the CTA card. */
    guestGalleryUploadUrl: z.string().nullable().default(null),
    /**
     * True only when this `PublicSite` was built by the in-app builder's
     * `previewSite()`. The production API always emits `false`; this gates
     * the Reveal placeholder so it never leaks to real guests.
     */
    previewMode: z.boolean().default(false),
    faq: z.array(FaqItemSchema),
    /** FAQ layout on the rendered site. */
    faqDisplayMode: FaqDisplayModeSchema.default("accordion"),
    /** When false, the hero image block is not rendered. Omitted = enabled. */
    heroImageEnabled: z.boolean().default(true),
    /** Resolved hero background image URL (from Firebase Storage). Null = none set. */
    heroImageUrl: z.string().nullable().optional(),
    /** Focal point for the hero image within its frame (%). */
    heroImagePosition: HeroImagePositionSchema.optional(),
    /** Font pair identifier for the rendered site. */
    fontPair: z.enum(["serif", "modern", "romantic"]).default("serif"),
    /** Per-section background color from the active theme palette. */
    sectionBackgrounds: z.record(WebsiteSectionIdSchema, WebsitePaletteColorSchema).default({}),
    /** Primary locale for couple-authored content (from wedding.settings.language). */
    primaryLocale: LocaleSchema.default("pl"),
    /** Locales guests may switch to. Always includes primaryLocale. */
    enabledLocales: z.array(LocaleSchema).default(["pl"]),
    /** Per-locale content overrides. Omitted fields fall back to primary at render time. */
    localized: z.record(LocaleSchema, WebsiteLocaleBundleSchema).default({}),
});
/** Stable guest-locale list: primary always included; empty config → primary only. */
export function resolveEffectiveEnabledLocales(primaryLocale, enabledLocales) {
    if (enabledLocales.length === 0)
        return [primaryLocale];
    const set = new Set(enabledLocales);
    set.add(primaryLocale);
    return LocaleSchema.options.filter((code) => set.has(code));
}
function mergeLocalizedContent(primary, override) {
    if (!override)
        return primary;
    const merged = { ...primary };
    for (const [key, value] of Object.entries(override)) {
        if (value != null && value.trim() !== "") {
            merged[key] = value;
        }
    }
    return merged;
}
/** Merge optional locale overrides onto primary content for guest rendering. */
export function resolveSiteForLocale(site, locale) {
    if (locale === site.primaryLocale)
        return site;
    const bundle = site.localized[locale];
    if (!bundle)
        return site;
    return {
        ...site,
        content: mergeLocalizedContent(site.content, bundle.content),
        storyTitle: bundle.storyTitle != null && bundle.storyTitle.trim() !== ""
            ? bundle.storyTitle
            : site.storyTitle,
        heroEyebrow: bundle.heroEyebrow !== undefined && bundle.heroEyebrow !== ""
            ? bundle.heroEyebrow
            : site.heroEyebrow,
        schedule: bundle.schedule?.length ? bundle.schedule : site.schedule,
        faq: bundle.faq?.length ? bundle.faq : site.faq,
    };
}
export const CountrySchema = z.string().length(2).describe("ISO 3166-1 alpha-2 (e.g. PL, DE, GB)");
/** Ceremony style captured at onboarding; drives day-plan template branching. */
export const CeremonyTypeSchema = z.enum(["church", "civil", "humanist", "other"]);
const CEREMONY_TYPES = CeremonyTypeSchema.options;
/** Unknown or missing values default to church (matches the legacy template). */
export function normalizeCeremonyType(raw) {
    if (raw && CEREMONY_TYPES.includes(raw)) {
        return raw;
    }
    return "church";
}
// ─── Day-plan people (calendar columns) ─────────────────────────────────────
/**
 * Earthy, muted column colours — harmonious on every palette. Assigned
 * round-robin by column order when a person is created. Ported from the
 * Claude Design prototype's PERSON_COLORS.
 */
export const DAY_PLAN_PALETTE = [
    "#c97b63",
    "#6f7d94",
    "#a87a8b",
    "#7d8c6a",
    "#b8956a",
    "#8a6a86",
    "#5f8a7d",
    "#b06a55",
];
/** Next palette colour for a column being appended at `index` (round-robin). */
export function dayPlanColorForIndex(index) {
    return DAY_PLAN_PALETTE[index % DAY_PLAN_PALETTE.length];
}
/**
 * A person/column in the wedding-day calendar planner (Panna Młoda, Pan
 * Młody, witnesses, parents, photographer, …). Stored as a small embedded
 * array on the wedding doc — tiny, bounded, rarely changes; same precedent
 * as `groups`. Events reference a person via `DayPlanEvent.personId`.
 */
export const DayPlanPersonSchema = z.object({
    id: z.string().min(1),
    /** Column heading, e.g. a partner's first name or "Świadek". */
    label: z.string().max(40),
    /** Icon key from PLAN_ICONS; degrades to a dot when unknown. */
    icon: z.string().default("dot"),
    /** Column accent colour (hex from DAY_PLAN_PALETTE, but free-form). */
    color: z.string(),
    /** Left-to-right position of the column. */
    order: z.number().int().default(0),
});
export const WeddingSettingsSchema = z.object({
    budget: z.number().int().nonnegative(),
    currency: CurrencySchema,
    timezone: z.string().default("Europe/Warsaw"),
    language: LocaleSchema,
    country: CountrySchema.default("PL"),
});
// ─── Third-party integrations ──────────────────────────────────────────────
export const GoogleCalendarIntegrationStatusSchema = z.enum([
    "connected",
    "needs_reauth",
    "disconnected",
]);
export const GoogleCalendarIntegrationSchema = z.object({
    status: GoogleCalendarIntegrationStatusSchema,
    connectedByUid: z.string(),
    connectedByEmail: z.string().email(),
    calendarId: z.string(),
    calendarSummary: z.string().default("Wesele"),
    connectedAt: z.string().datetime(),
    lastSyncAt: z.string().datetime().nullable().optional(),
    lastSyncError: z.string().nullable().optional(),
});
export const WeddingIntegrationsSchema = z.object({
    googleCalendar: GoogleCalendarIntegrationSchema.optional(),
});
// ─── Menu card (printable) ─────────────────────────────────────────────────
export const MenuCardStyleSchema = z.enum(["editorial", "minimal", "botanical"]);
/** How the Editorial style renders the couple at the top of the card. */
export const MenuCardEditorialCoupleDisplaySchema = z.enum(["initials", "full"]);
export const MenuCardPaletteSchema = z.enum(["rose", "bone", "forest", "blush"]);
/**
 * Physical cut size on the printed sheet. The card design itself stays
 * at its native 1:2 aspect; the cell around it grows wider so the
 * trimmed card carries more solid-colour bleed on either side.
 *
 *   s — 74 × 148 mm (today's default; matches the card's native width)
 *   m — 90 × 148 mm
 *   l — 105 × 148 mm (fills a full A6 cell; the four cards touch)
 */
export const MenuCardSizeSchema = z.enum(["s", "m", "l"]);
export const MenuCourseTagSchema = z.enum(["v", "ve", "gf"]);
export const MenuCourseKindSchema = z.enum([
    "welcome",
    "starter",
    "soup",
    "main",
    "main-alt",
    "dessert",
    "late",
    "extra",
]);
export const MenuCourseSchema = z.object({
    id: z.string().min(1),
    kind: MenuCourseKindSchema.default("extra"),
    label: z.string().max(40),
    name: z.string().max(160).default(""),
    /** Optional second dish offered as an alternative for this course (e.g. two soups). */
    nameAlt: z.string().max(160).optional(),
    tags: z.array(MenuCourseTagSchema).default([]),
});
/** Six-digit hex (`#rrggbb`) — the value `<input type="color">` returns natively. */
export const HexColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Expected hex colour like #fbf2f0");
export const MenuCardSchema = z.object({
    style: MenuCardStyleSchema.default("editorial"),
    palette: MenuCardPaletteSchema.default("rose"),
    /** Card cut size on the printed sheet. */
    cardSize: MenuCardSizeSchema.default("s"),
    /** When set, overrides the palette's background colour on the rendered card. */
    customBg: HexColorSchema.nullable().optional(),
    coupleA: z.string().max(40),
    coupleB: z.string().max(40),
    date: z.string().max(60),
    place: z.string().max(80),
    courses: z.array(MenuCourseSchema).max(20),
    perGuest: z.boolean().default(false),
    /** Editorial header: monogram initials vs full couple names. */
    editorialCoupleDisplay: MenuCardEditorialCoupleDisplaySchema.default("initials"),
    updatedAt: z.string().datetime().optional(),
});
export const WeddingSchema = z.object({
    id: z.string(),
    ownerId: z.string(),
    partnerId: z.string().nullable().optional(),
    /** Display name of the partner who accepted the co-planning invite. */
    partnerName: z.string().nullable().optional(),
    /** Token of the accepted partner invite — the proof the wedding rule checks. */
    partnerInviteToken: z.string().nullable().optional(),
    title: z.string(),
    coupleName1: z.string(),
    coupleName2: z.string(),
    weddingDate: z.string().datetime().describe("ISO timestamp; must be in the future on create"),
    venue: z.string().nullable().optional(),
    /** Whether the couple has already booked their venue. Used by the AI task-template wizard. */
    venueBooked: z.boolean().optional(),
    /** Church vs civil/humanist/other — used by the day-plan template seed. */
    ceremonyType: CeremonyTypeSchema.optional(),
    estimatedGuestCount: z.number().int().positive(),
    guestCount: z.number().int().nonnegative().default(0).describe("Computed: actual added guests"),
    /** Invitation heads for free-tier limit — mirrors countAttendees() on the guest list. */
    attendeeCount: z.number().int().nonnegative().default(0),
    handle: z
        .string()
        .regex(/^[a-z0-9-]+$/)
        .describe("Subdomain handle for [handle].amazing.wedding"),
    settings: WeddingSettingsSchema,
    website: WebsiteConfigSchema.optional(),
    /** Optional seating planner room border (percent insets). Omitted = default margins. */
    seatingCanvasInset: SeatingCanvasInsetSchema.optional(),
    /**
     * Real-world width of the seating room in metres.  The room height (also in
     * metres) is always derived: `roomHeightM = seatingRoomWidthM × (roomH_px / roomW_px)`
     * so the scale stays uniform on both axes.  Default 20 m (typical banquet hall).
     */
    seatingRoomWidthM: z.number().positive().default(20).optional(),
    /** Active seating plan version — shared by both partners. */
    activeSeatingPlanId: z.string().nullable().optional(),
    /** Plan that absorbed pre-migration flat guest.tableId assignments. */
    legacySeatingPlanId: z.string().nullable().optional(),
    /** True once legacy /tables data has been migrated to seatingPlans. */
    seatingMigrationDone: z.boolean().optional(),
    /** Client migration lock — prevents concurrent double-migration. */
    seatingMigrationInProgress: z.boolean().optional(),
    groups: z
        .array(GuestGroupSchema)
        .default([])
        .describe("Embedded guest groups — small list, rarely changes after onboarding"),
    /** Third-party integrations configured on this wedding (e.g. Google Calendar). */
    integrations: WeddingIntegrationsSchema.optional(),
    /**
     * Columns for the wedding-day calendar planner. Empty/absent = not yet
     * seeded; the screen seeds the two default couple columns on first visit.
     */
    dayPlanPeople: z.array(DayPlanPersonSchema).default([]).optional(),
    /**
     * Unguessable token embedded in the public day-plan ICS feed URL
     * (`/api/day-plan/{id}/{token}.ics`) so Google/Apple Calendar can subscribe.
     * Absent/empty = feed disabled. Rotate to revoke a shared link.
     */
    dayPlanFeedToken: z.string().optional(),
    /** Saved configuration for the printable menu-card designer. */
    menuCard: MenuCardSchema.nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CreateWeddingSchema = WeddingSchema.pick({
    coupleName1: true,
    coupleName2: true,
    weddingDate: true,
    estimatedGuestCount: true,
    handle: true,
}).extend({
    budget: z.number().int().nonnegative(),
    country: CountrySchema,
    language: LocaleSchema,
    currency: CurrencySchema.default("PLN"),
});
